import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Building2, Receipt, Mail, Phone, Landmark } from "lucide-react";
import { format, parseISO } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function inr(n) {
  return "₹" + (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const statusColors = {
  Unpaid: "bg-red-100 text-red-700 border-red-200",
  Partial: "bg-amber-100 text-amber-700 border-amber-200",
  Paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function PublicInvoice() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/invoices/${id}`)
      .then(({ data }) => setData(data))
      .catch(e => setErr(e.response?.data?.detail || "Invoice not found"));
  }, [id]);

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <Card className="p-10 max-w-md text-center">
        <Receipt className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <div className="font-semibold text-lg">Invoice not available</div>
        <div className="text-sm text-muted-foreground mt-2">{err}</div>
      </Card>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="text-sm text-muted-foreground font-mono">Loading invoice…</div>
    </div>
  );

  const { invoice: inv, client_name, firm } = data;
  const overdue = inv.balance_amount > 0 && inv.due_date < new Date().toISOString().slice(0, 10);
  const pdfUrl = `${BACKEND_URL}/api/public/invoices/${id}/pdf`;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="public-invoice-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Firm header */}
        <div className="text-center">
          <div className="inline-flex w-12 h-12 rounded-md bg-blue-600 text-white items-center justify-center font-display font-bold text-xl">
            {(firm?.name || "V")[0]}
          </div>
          <h1 className="font-display text-2xl font-bold mt-3 tracking-tight">{firm?.name}</h1>
          <div className="text-xs text-muted-foreground uppercase tracking-widest">{firm?.tagline}</div>
        </div>

        {/* Invoice card */}
        <Card className="p-8" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="flex items-start justify-between border-b border-border pb-5 mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Invoice</div>
              <div className="font-mono font-bold text-xl mt-1">{inv.number}</div>
            </div>
            <Badge className={`${statusColors[inv.status]} border text-xs px-3 py-1`}>{inv.status}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-6 text-sm mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Bill To</div>
              <div className="font-semibold">{client_name}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Dates</div>
              <div className="font-mono text-xs">Issued: {inv.issue_date}</div>
              <div className={`font-mono text-xs ${overdue ? "text-red-600 font-semibold" : ""}`}>Due: {inv.due_date}</div>
            </div>
          </div>

          {/* Description */}
          <div className="border border-border rounded-md p-4 mb-6 bg-slate-50">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Description</div>
            <div className="text-sm">{inv.description || "Professional services"}</div>
          </div>

          {/* Amount block */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono tabular-nums">{inr(inv.amount)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">GST @ {inv.gst_rate}%</span><span className="font-mono tabular-nums">{inr(inv.gst_amount)}</span></div>
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
              <span>Total</span><span className="font-mono tabular-nums">{inr(inv.total_amount)}</span>
            </div>
            {inv.paid_amount > 0 && (
              <>
                <div className="flex justify-between text-emerald-600"><span>Paid</span><span className="font-mono tabular-nums">{inr(inv.paid_amount)}</span></div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-red-500/40 text-red-600">
                  <span>Balance Due</span><span className="font-mono tabular-nums">{inr(inv.balance_amount)}</span>
                </div>
              </>
            )}
          </div>

          {/* Payments */}
          {inv.payments && inv.payments.length > 0 && (
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Payment History</div>
              <div className="border border-border rounded-md divide-y divide-border text-xs">
                {inv.payments.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="font-mono tabular-nums font-semibold w-24">{inr(p.amount)}</span>
                    <span className="text-muted-foreground">{p.date}</span>
                    <Badge variant="outline" className="text-[10px]">{p.mode}</Badge>
                    {p.reference && <span className="font-mono text-muted-foreground truncate">{p.reference}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PDF button */}
          <div className="mt-8 pt-6 border-t border-border flex justify-center">
            <Button data-testid="download-pdf-btn" size="lg" className="bg-blue-600 hover:bg-blue-700" asChild>
              <a href={pdfUrl} target="_blank" rel="noreferrer" download>
                <Download className="w-4 h-4 mr-2" /> Download PDF Invoice
              </a>
            </Button>
          </div>
        </Card>

        {/* Footer */}
        <Card className="p-6 text-sm">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Payment Instructions</div>
          {firm?.bank_details && (
            <div className="flex items-start gap-2.5 mb-3">
              <Landmark className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div className="font-mono text-xs">{firm.bank_details}</div>
            </div>
          )}
          <div className="text-xs text-muted-foreground space-y-1 pt-3 border-t border-border">
            <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> {firm?.email}</div>
            <div className="flex items-center gap-2"><Phone className="w-3 h-3" /> {firm?.phone}</div>
          </div>
        </Card>

        <div className="text-center text-[10px] text-muted-foreground uppercase tracking-widest">
          Powered by TPMS · Tax Practice OS
        </div>
      </div>
    </div>
  );
}
